import { createId } from '@paralleldrive/cuid2'
import { createContext, type JSX, useCallback, useContext } from 'react'
import { useImmer } from 'use-immer'

export type ToastVariant = 'success' | 'warning' | 'error' | 'info' | 'loading'

// We don't need `.options` for this at the moment, so it can be a simple type instead of inferred from a Zod enum.
type ToastTransience = 'default' | 'sticky'

export type Toast = {
  id: string
  message: JSX.Element
  variant: ToastVariant
  transience?: ToastTransience
}

export type ToastService = {
  toasts: Map<Toast['id'], Toast>
  addToast: (toast: Omit<Toast, 'id'>) => Toast
  removeToast: (id: Toast['id']) => unknown
}

const ToastServiceContext = createContext<ToastService | undefined>(undefined)

export const useToastService = () => {
  const service = useContext(ToastServiceContext)
  if (!service) {
    throw new Error('useToastService must be used within a ToastServiceProvider')
  }

  return service
}

const useToastServiceImplementation = () => {
  const [toasts, updateToasts] = useImmer<ToastService['toasts']>(new Map())

  const addToast = useCallback(
    (toast => {
      const id = createId()
      const fullToast = { ...toast, id }
      updateToasts(draft => {
        draft.set(fullToast.id, fullToast)
      })

      return fullToast
    }) satisfies ToastService['addToast'],
    [updateToasts]
  )

  const removeToast = useCallback(
    (id => {
      updateToasts(draft => {
        draft.delete(id)
      })
    }) satisfies ToastService['removeToast'],
    [updateToasts]
  )

  return {
    toasts,
    addToast,
    removeToast,
  } satisfies ToastService
}

export type ToastServiceProviderProps = React.PropsWithChildren<{ override?: ToastService }>

export const ToastServiceProvider: React.FC<ToastServiceProviderProps> = ({ children, override }) => {
  const service = useToastServiceImplementation()

  return <ToastServiceContext value={override ?? service}>{children}</ToastServiceContext>
}
